/**
 * Date-related utility functions for the restaurant application
 */

import { fromZonedTime, toZonedTime } from 'date-fns-tz';

const RESTAURANT_TZ = 'America/Chicago';

// A global or module-level counter to ensure each new Date is unique
let creationCounter = 0;

/**
 * Returns a Date guaranteed to be unique across multiple calls in the same millisecond
 */
export function getUniqueCreatedAt(): Date {
  creationCounter++;
  // Add 1 ms per new guest
  return new Date(Date.now() + creationCounter);
}

/**
 * Returns a Date on "today" made from an HH:MM string
 */
export const hhmmToToday = (hhmm: string): Date => {
  const [h, m] = hhmm.split(':').map(Number);
  // Build a date in the restaurant's local time zone and convert to UTC Date
  const todayISOChicago = new Date().toLocaleDateString('en-CA', { timeZone: RESTAURANT_TZ }); // yyyy-mm-dd
  const chicagoDateStr = `${todayISOChicago} ${hhmm}`; // "2025-05-10 13:45"
  const result = fromZonedTime(chicagoDateStr, RESTAURANT_TZ);

  // Ensure we return a proper Date object
  if (!(result instanceof Date) || isNaN(result.getTime())) {
    console.error('hhmmToToday: Invalid Date result from fromZonedTime', { hhmm, chicagoDateStr, result });
    // Fallback: create a new Date with the correct hours/minutes
    const now = new Date();
    now.setHours(h, m, 0, 0);
    return now;
  }

  return result;
};

/**
 * Returns a Date on a specific date made from an HH:MM string and a date string (YYYY-MM-DD)
 * Used for future date bookings where we need to set the time on a specific date
 */
export const hhmmToDate = (hhmm: string, dateStr: string): Date => {
  const [h, m] = hhmm.split(':').map(Number);
  // Build a date in the restaurant's local time zone and convert to UTC Date
  const chicagoDateStr = `${dateStr} ${hhmm}`; // "2025-05-15 13:45"
  const result = fromZonedTime(chicagoDateStr, RESTAURANT_TZ);

  // Ensure we return a proper Date object
  if (!(result instanceof Date) || isNaN(result.getTime())) {
    console.error('hhmmToDate: Invalid Date result from fromZonedTime', { hhmm, dateStr, chicagoDateStr, result });
    // Fallback: create a new Date with the correct hours/minutes on the given date
    const [year, month, day] = dateStr.split('-').map(Number);
    const fallback = new Date(year, month - 1, day, h, m, 0, 0);
    return fallback;
  }

  return result;
};

/**
 * Format a Date to 12-hour time format (h:mm AM/PM)
 */
export function formatTime12h(d: Date): string {
  // Always format using the restaurant's local time zone
  const local = toZonedTime(d, RESTAURANT_TZ);
  let h = local.getHours();
  const m = String(local.getMinutes()).padStart(2, '0');
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${h}:${m} ${ampm}`;
}

/**
 * Format a Date to 24-hour time format (HH:MM)
 */
export const formatHHMM = (d: Date): string => {
  // Always format using the restaurant's local time zone
  const local = toZonedTime(d, RESTAURANT_TZ);
  return `${local.getHours().toString().padStart(2, '0')}:${local.getMinutes().toString().padStart(2, '0')}`;
};

/**
 * Convert 24-hour format time string to 12-hour format time string
 * Also handles Date objects and ISO date strings
 */
export const formatTimeDisplay = (time: string | Date): string => {
  if (!time) return 'No time specified';

  // Handle Date objects or ISO strings (contains 'T' or '-')
  if (time instanceof Date || (typeof time === 'string' && (time.includes('T') || time.includes('-')))) {
    const date = time instanceof Date ? time : new Date(time);
    return formatTime12h(date); // Use existing timezone-aware formatter
  }

  // Handle HH:MM strings
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
};

/**
 * Add minutes to a time string in format HH:MM
 */
export const addMinutes = (time: string, minutesToAdd: number): string => {
  const [hours, minutes] = time.split(':').map(Number);
  const date = new Date();
  date.setHours(hours, minutes + minutesToAdd);
  return formatHHMM(date);
};

/**
 * Takes the full seat window "start -> end" in Date form,
 * returns a user-facing [start, end] that ensures the user
 * doesn't wait more than maxMinutes minutes in the restaurant.
 */
/** the on-site window ceiling the quote service enforces; the legacy
 * 20-minute, localStorage-overridable cap is gone (seating rebuild item 6) */
export const ON_SITE_CAP_DEFAULT_MIN = 15;

export function capOnSiteWait(
  start: Date,
  end: Date,
  maxMinutes?: number
): { newStart: Date; newEnd: Date } {
  const cap = typeof maxMinutes === 'number' ? maxMinutes : ON_SITE_CAP_DEFAULT_MIN;
  const waitMs = end.getTime() - start.getTime();
  const waitMin = waitMs / 60000;

  if (waitMin <= cap) {
    // No change needed
    return { newStart: start, newEnd: end };
  }

  // Shift the start time forward so the total is cap minutes
  const newStartMs = end.getTime() - cap * 60000;
  const newStart = new Date(newStartMs);
  return { newStart, newEnd: end };
}

/**
 * Safely get time value from a Date object or string
 */
export const getTimeValue = (date: Date | string | any): number => {
  if (date instanceof Date) {
    return date.getTime();
  } else if (typeof date === 'string') {
    return new Date(date).getTime();
  }
  // Fallback to current time if date is invalid
  return new Date().getTime();
};

/**
 * Converts a time string in format "HH:MM" to a Date object set to today's date
 * @param timeString - Time string in 24-hour format (e.g., "14:30")
 * @returns Date object with the time set to the provided time string
 */
export const timeStringToDate = (timeString: string): Date => {
  return hhmmToToday(timeString);
};

/**
 * Returns the current Date adjusted to the restaurant's timezone.
 * Using `toZonedTime` ensures the resulting Date's getters (getHours etc.)
 * reflect America/Chicago wall-clock time no matter where the browser is.
 */
export function getChicagoNow(): Date {
  return toZonedTime(new Date(), RESTAURANT_TZ);
}

/**
 * Returns today's date (America/Chicago) in ISO `yyyy-MM-dd` format.
 * We use Intl to avoid daylight-saving pitfalls and ensure the
 * result matches what the restaurant considers "today", not UTC.
 */
/**
 * Categorize a timestamp into a relative-time bucket. The caller resolves the
 * bucket against i18n strings so display copy stays translatable:
 *   const { key, count } = getRelativeTimeKey(thread.updatedAt);
 *   t(`time.${key}`, { count })  // -> "5m ago" / "hace 5m" / "just now"
 */
export function getRelativeTimeKey(date: Date | string): {
  key: 'just_now' | 'minutes_ago' | 'hours_ago' | 'days_ago';
  count: number;
} {
  const d = typeof date === 'string' ? new Date(date) : date;
  const diffSec = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);
  if (diffSec < 60) return { key: 'just_now', count: 0 };
  if (diffMin < 60) return { key: 'minutes_ago', count: diffMin };
  if (diffHour < 24) return { key: 'hours_ago', count: diffHour };
  return { key: 'days_ago', count: diffDay };
}

/**
 * How far ahead a party can be booked or browsed, in days. This is the ONLY
 * limit on the horizon: the server accepts any future date (validateGuestData
 * downgrades "far out" to a warning, and the POST route logs warnings without
 * blocking), so raising this number is all it takes to open the calendar
 * further. Shared by the planner's browser + edit sheet and the staff app's
 * call-ahead form so the three can never disagree about how far out you can
 * see versus book. Does not apply to the public /call-ahead page, which has
 * no horizon cap of its own.
 *
 * A full year ahead (owner request, 2026-09-22): parties book anniversaries
 * and holidays further out than the six months this used to stop at. 366 and
 * not 365 so "this same date next year" is reachable on every day of the
 * calendar — from a day whose next twelve months contain a Feb 29, 365 lands
 * one day short of it.
 */
export const MAX_FUTURE_BOOKING_DAYS = 366;

/**
 * How far BACK the planner may browse, in days. Deliberately separate from the
 * forward horizon: looking back is a read of guest history, not a booking
 * window, and the two have no reason to move together. Stays at 180 now that
 * the forward horizon has opened to a year: a planner needs to book far ahead,
 * not to read further back into guest history.
 *
 * The server enforces this same bound for planner sessions (see
 * plannerDateInReadRange) — the picker's min is a convenience, not the control.
 */
export const MAX_PAST_VIEWING_DAYS = 180;

/**
 * Today's date (America/Chicago) plus N days, as ISO `yyyy-MM-dd`. Negative
 * values are allowed (returns N days *ago*). Caller can compose with
 * hhmmToDate to get a Chicago-time Date at a specific hour, e.g. for
 * "Tomorrow at 2:30 PM" defaults on the task due-date sheet.
 */
export function addDaysChicagoISO(n: number): string {
  const today = getChicagoTodayISO();           // "2026-05-19"
  const [y, m, d] = today.split('-').map(Number);
  const base = new Date(Date.UTC(y, m - 1, d));
  base.setUTCDate(base.getUTCDate() + n);
  const yy = base.getUTCFullYear();
  const mm = String(base.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(base.getUTCDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

export function getChicagoTodayISO(): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: RESTAURANT_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());

  const y = parts.find(p => p.type === 'year')?.value;
  const m = parts.find(p => p.type === 'month')?.value;
  const d = parts.find(p => p.type === 'day')?.value;

  return `${y}-${m}-${d}`;
}

/**
 * "2026-08-15" -> "Saturday, August 15". TZ-proof: the ISO string is treated
 * as a civil date anchored at UTC, so the weekday can never shift regardless
 * of the server's or browser's zone. Used in guest-facing Guestlist SMS copy.
 */
export function formatVisitDateHuman(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', timeZone: 'UTC',
  });
}

/**
 * Tolerant "HH:MM" / "H:MM AM|PM" -> "h:mm AM" 12-hour display string.
 * Returns null when unparseable so callers can omit the time clause.
 */
export function formatHHMMDisplay(t: string | null | undefined): string | null {
  if (!t) return null;
  const m = t.trim().toUpperCase().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/);
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const min = m[2];
  if (m[3] === 'PM' && h < 12) h += 12;
  if (m[3] === 'AM' && h === 12) h = 0;
  if (h > 23 || parseInt(min, 10) > 59) return null;
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${min} ${ampm}`;
}